import React, { type ReactNode } from 'react';

import {
    type AdminAgentArtifact,
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    type AdminArtifactRendererProps,
    citationsFromRecord,
    firstArray,
    firstString,
    isRecord,
    renderUntrustedString,
    scalarString,
    stringsFromUnknown,
    toRecord,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';

type AdminEvidenceBundleVerdict = {
    readonly label: string;
    readonly detail: string | null;
    readonly confidence: string | null;
    readonly citations: ReadonlyArray<string>;
};

type AdminEvidenceBundleItem = {
    readonly title: string | null;
    readonly detail: string | null;
    readonly kind: string | null;
    readonly source: string | null;
    readonly confidence: string | null;
    readonly citations: ReadonlyArray<string>;
};

type AdminEvidenceBundleSection = {
    readonly title: string;
    readonly items: ReadonlyArray<AdminEvidenceBundleItem>;
};

type AdminEvidenceBundlePayload = {
    readonly verdict: AdminEvidenceBundleVerdict | null;
    readonly sections: ReadonlyArray<AdminEvidenceBundleSection>;
};

export function AdminEvidenceBundleArtifact({
    artifact,
}: AdminArtifactRendererProps) {
    const bundle = getEvidenceBundlePayload(artifact.payload);
    const evidenceItemCount = bundle.sections.reduce(
        (count, section) => count + section.items.length,
        0,
    );
    return (
        <ArtifactFrame
            artifact={artifact}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            <ArtifactEvidence artifact={artifact} />
            {bundle.verdict ? (
                <EvidenceBundleVerdictPanel verdict={bundle.verdict} />
            ) : null}
            {evidenceItemCount === 0 ? (
                <p className="text-muted-foreground text-xs">
                    No evidence items were present in this artifact.
                </p>
            ) : (
                <div className="space-y-3">
                    {bundle.sections.map((section, index) => (
                        <EvidenceBundleSectionView
                            key={`${section.title}-${index}`}
                            section={section}
                        />
                    ))}
                </div>
            )}
        </ArtifactFrame>
    );
}

function EvidenceBundleVerdictPanel({
    verdict,
}: {
    readonly verdict: AdminEvidenceBundleVerdict;
}) {
    return (
        <div className="border-border space-y-1 border-l-2 pl-3">
            <div className="text-muted-foreground/70 text-[11px]">Verdict</div>
            <div className="text-foreground text-[13px] font-medium tracking-[-0.01em]">
                {renderUntrustedString(verdict.label)}
            </div>
            {verdict.detail ? (
                <p className="text-muted-foreground text-xs leading-relaxed">
                    {renderUntrustedString(verdict.detail)}
                </p>
            ) : null}
            <EvidenceBundleMetadata
                confidence={verdict.confidence}
                citations={verdict.citations}
                source={null}
            />
        </div>
    );
}

function EvidenceBundleSectionView({
    section,
}: {
    readonly section: AdminEvidenceBundleSection;
}) {
    return (
        <section className="border-border/40 space-y-2 border-t pt-2.5 first:border-t-0 first:pt-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h5 className="text-foreground text-xs font-medium">
                    {renderUntrustedString(section.title)}
                </h5>
                <span className="text-muted-foreground/60 text-[11px] tabular-nums">
                    {section.items.length}{' '}
                    {section.items.length === 1 ? 'item' : 'items'}
                </span>
            </div>
            <ul className="space-y-2">
                {section.items.map((item, index) => (
                    <EvidenceBundleItemView
                        key={`${item.title ?? section.title}-${index}`}
                        item={item}
                    />
                ))}
            </ul>
        </section>
    );
}

function EvidenceBundleItemView({
    item,
}: {
    readonly item: AdminEvidenceBundleItem;
}) {
    return (
        <li className="border-border space-y-1 border-l pl-2.5">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
                {item.title ? (
                    <span className="text-foreground text-xs font-medium">
                        {renderUntrustedString(item.title)}
                    </span>
                ) : null}
                {item.kind ? (
                    <span className="text-muted-foreground/70 text-[11px]">
                        {renderUntrustedString(item.kind)}
                    </span>
                ) : null}
            </div>
            {item.detail ? (
                <p className="text-muted-foreground text-xs leading-relaxed break-words">
                    {renderUntrustedString(item.detail)}
                </p>
            ) : null}
            <EvidenceBundleMetadata
                confidence={item.confidence}
                citations={item.citations}
                source={item.source}
            />
        </li>
    );
}

function EvidenceBundleMetadata({
    confidence,
    citations,
    source,
}: {
    readonly confidence: string | null;
    readonly citations: ReadonlyArray<string>;
    readonly source: string | null;
}) {
    if (!confidence && !source && citations.length === 0) {
        return null;
    }
    const parts: Array<ReactNode> = [];
    if (source) {
        parts.push(renderUntrustedString(source));
    }
    if (confidence) {
        parts.push(<span>{renderUntrustedString(confidence)} confidence</span>);
    }
    for (const citation of citations) {
        parts.push(
            <span className="font-mono break-all">
                {renderUntrustedString(citation)}
            </span>,
        );
    }
    return (
        <p className="text-muted-foreground/70 text-[11px]">
            {parts.map((part, index) => (
                <React.Fragment key={index}>
                    {index > 0 ? (
                        <span className="text-muted-foreground/40">
                            {' · '}
                        </span>
                    ) : null}
                    {part}
                </React.Fragment>
            ))}
        </p>
    );
}

function getEvidenceBundlePayload(
    payload: AdminAgentArtifact['payload'],
): AdminEvidenceBundlePayload {
    const data = toRecord(payload);
    return {
        verdict: evidenceBundleVerdictFromUnknown(
            data['verdict'] ?? data['resolution_verdict'],
        ),
        sections: getEvidenceBundleSections(data),
    };
}

function getEvidenceBundleSections(
    payload: Record<string, unknown>,
): ReadonlyArray<AdminEvidenceBundleSection> {
    const sections = [
        ...evidenceBundleSectionsFromUnknown(payload['sections']),
    ];
    appendEvidenceBundleSection(
        sections,
        'Evidence',
        firstArray(payload, ['evidence_items', 'evidence', 'items']),
    );
    appendEvidenceBundleSection(sections, 'Facts', payload['facts']);
    appendEvidenceBundleSection(sections, 'Diagnoses', payload['diagnoses']);
    appendEvidenceBundleSection(
        sections,
        'Trace annotations',
        payload['annotations'],
    );
    appendEvidenceBundleSection(sections, 'Caveats', payload['caveats']);
    appendEvidenceBundleSection(sections, 'Next steps', payload['next_steps']);
    return sections;
}

function appendEvidenceBundleSection(
    sections: Array<AdminEvidenceBundleSection>,
    title: string,
    value: unknown,
) {
    const items = evidenceBundleItemsFromUnknown(value);
    if (items.length > 0) {
        sections.push({ title, items });
    }
}

function evidenceBundleSectionsFromUnknown(
    value: unknown,
): ReadonlyArray<AdminEvidenceBundleSection> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).flatMap((section) => {
        const items =
            evidenceBundleItemsFromUnknown(section['items']).length > 0
                ? evidenceBundleItemsFromUnknown(section['items'])
                : evidenceBundleItemsFromUnknown(
                      section['evidence'] ?? section['rows'],
                  );
        if (items.length === 0) {
            return [];
        }
        return [
            {
                title:
                    firstString(section, ['title', 'label', 'name']) ??
                    'Evidence',
                items,
            },
        ];
    });
}

function evidenceBundleItemsFromUnknown(
    value: unknown,
): ReadonlyArray<AdminEvidenceBundleItem> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.flatMap((item) => {
        const parsedItem = evidenceBundleItemFromUnknown(item);
        return parsedItem ? [parsedItem] : [];
    });
}

function evidenceBundleItemFromUnknown(
    value: unknown,
): AdminEvidenceBundleItem | null {
    if (typeof value === 'string' && value.trim()) {
        return {
            title: null,
            detail: value,
            kind: null,
            source: null,
            confidence: null,
            citations: [],
        };
    }
    if (!isRecord(value)) {
        return null;
    }
    const evidenceText = stringsFromUnknown(value['evidence']).join(', ');
    const primaryDetail = firstString(value, [
        'detail',
        'statement',
        'summary',
        'reason',
        'explanation',
        'fact',
        'description',
    ]);
    const detail = primaryDetail
        ? [primaryDetail, evidenceText ? `Evidence: ${evidenceText}` : null]
              .filter((part) => part)
              .join(' ')
        : evidenceText || null;
    const title = firstString(value, [
        'title',
        'label',
        'name',
        'category',
        'kind',
        'source_table',
        'source_name',
    ]);
    if (!title && !detail) {
        return null;
    }
    return {
        title,
        detail,
        kind: firstString(value, ['kind', 'type']),
        source: firstString(value, ['source', 'source_table', 'source_name']),
        confidence: scalarString(value['confidence']),
        citations: citationsFromRecord(value),
    };
}

function evidenceBundleVerdictFromUnknown(
    value: unknown,
): AdminEvidenceBundleVerdict | null {
    if (typeof value === 'string' && value.trim()) {
        return {
            label: value,
            detail: null,
            confidence: null,
            citations: [],
        };
    }
    if (!isRecord(value)) {
        return null;
    }
    const label = firstString(value, [
        'label',
        'verdict',
        'status',
        'outcome',
        'title',
    ]);
    const detail = firstString(value, [
        'detail',
        'summary',
        'reason',
        'statement',
        'explanation',
    ]);
    if (!label && !detail) {
        return null;
    }
    return {
        label: label ?? 'Verdict',
        detail,
        confidence: scalarString(value['confidence']),
        citations: citationsFromRecord(value),
    };
}
